import { Cog, Leaf } from "lucide-react";
import { useGetRecipeIngredient } from "../../../../api/recipe-ingredients";
import { useGetRecipeSpecificGenericProcess } from "../../../../api/recipe-specific-generic-process";
import { Button } from "../../../../components/ui/common/button";

interface CompanyAllowCountButtonProps {
  kind: "ingredients" | "processes";
  tenantId?: string;
  onClick: () => void;
}

/**
 * Pill button showing how many recipe ingredients / generic processes a
 * company is allowed ("Ingredients 2", "Processes 1"); clicking opens the
 * matching picker. The counts come from the same queries (and cache keys)
 * the pickers use, so opening a picker doesn't refetch, and saving one
 * doesn't leave a stale count behind once the query refetches.
 */
const CompanyAllowCountButton = ({ kind, tenantId, onClick }: CompanyAllowCountButtonProps) => {
  const ingredients = useGetRecipeIngredient(tenantId as string);
  const processes = useGetRecipeSpecificGenericProcess(tenantId as string);
  const query = kind === "ingredients" ? ingredients : processes;
  const count = Array.isArray(query.data) ? query.data.length : undefined;
  const Icon = kind === "ingredients" ? Leaf : Cog;

  return (
    <Button
      variant="outline"
      size="sm"
      className="tw-h-8 tw-gap-1.5 tw-bg-muted/40 tw-px-3 tw-text-xs tw-font-normal"
      onClick={onClick}
    >
      <Icon className="tw-h-3.5 tw-w-3.5 tw-text-muted-foreground" />
      <span>{kind === "ingredients" ? "Ingredients" : "Processes"}</span>
      <span className="tw-font-mono">{count ?? "–"}</span>
    </Button>
  );
};

export default CompanyAllowCountButton;
